

/**
 * Utility function to generate a consistent color from a string (like a name)
 * This is useful for generating avatar background colors
 * @param {string} string - Any string input (e.g. a name)
 * @returns {string} - A hex color code
 */
export function stringToColor(string) {
  if (!string) return '#757575'; // Default gray for empty strings
  
  let hash = 0;
  let i;

  // Generate a hash value from the string
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert the hash to a hex color
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  // Generate more vibrant colors with better contrast
  const colorAdjusted = adjustColorBrightness(color);
  return colorAdjusted;
}

/**
 * Adjust color brightness to ensure good contrast and vibrant colors
 * @param {string} color - Hex color code
 * @returns {string} - Adjusted hex color code
 */
function adjustColorBrightness(color) {
  // Convert hex to RGB
  let r = parseInt(color.slice(1, 3), 16);
  let g = parseInt(color.slice(3, 5), 16);
  let b = parseInt(color.slice(5, 7), 16);
  
  // Calculate brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  
  // Adjust colors to ensure they're not too dark or too light
  const minBrightness = 60;  // Minimum brightness
  const maxBrightness = 200; // Maximum brightness
  
  if (brightness < minBrightness) {
    const factor = minBrightness / Math.max(brightness, 1);
    r = Math.min(255, Math.round(r * factor));
    g = Math.min(255, Math.round(g * factor));
    b = Math.min(255, Math.round(b * factor));
  } else if (brightness > maxBrightness) {
    const factor = maxBrightness / brightness;
    r = Math.round(r * factor);
    g = Math.round(g * factor);
    b = Math.round(b * factor);
  }
  
  // Increase saturation slightly for more vibrant colors
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max !== min) {
    const saturationFactor = 1.2;
    const mid = (max + min) / 2;
    
    if (r !== max && r !== min) r = mid + (r - mid) * saturationFactor;
    if (g !== max && g !== min) g = mid + (g - mid) * saturationFactor;
    if (b !== max && b !== min) b = mid + (b - mid) * saturationFactor;
    
    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));
  }
  
  // Convert back to hex
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Get contrasting text color (black or white) based on background color
 * @param {string} hexColor - Hex color code for background
 * @returns {string} - '#ffffff' or '#000000' for optimal contrast
 */
export function getContrastText(hexColor) {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate luminance (perceived brightness)
  // Using the formula from WCAG 2.0
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return black for light colors, white for dark colors
  return luminance > 0.5 ? '#000000' : '#ffffff';
} 